import { useCallback } from "react";
import type { LocationGenerics } from "@/router/locationGenerics";

import { useNavigate, useSearch } from "@tanstack/react-location";

type ViewDataSourceState = NonNullable<
  LocationGenerics["Search"]["viewDataSource"]
>;
type SlideOverTab = NonNullable<ViewDataSourceState["tab"]>;

type SlideOverInputOptions = {
  syncId: string;
  sourceStreamName?: string;
  tab?: SlideOverTab;
  run?: string;
};

/** Normalize a legacy string syncId into the object shape. */
export function normalizeViewDataSource(
  vds: unknown,
): ViewDataSourceState | undefined {
  if (typeof vds === "string") return { syncId: vds };
  if (typeof vds === "object" && vds != null) return vds as ViewDataSourceState;
  return undefined;
}

export function useViewDataSourceSlideOver() {
  const navigate = useNavigate<LocationGenerics>();
  return {
    onOpen: useCallback(
      (options: SlideOverInputOptions) => {
        navigate({
          search: (old) => {
            return {
              ...old,
              viewDataSource: {
                syncId: options.syncId,
                tab: options.tab,
                table: options.sourceStreamName,
                run: options.run,
              },
            };
          },
        });
      },
      [navigate],
    ),
    onClose: useCallback(() => {
      navigate({
        search: (old) => {
          return {
            ...old,
            viewDataSource: undefined,
          };
        },
      });
    }, [navigate]),
    setTab: useCallback(
      (tab: SlideOverTab | undefined) => {
        navigate({
          search: (old) => {
            const vds = normalizeViewDataSource(old?.viewDataSource);
            return {
              ...old,
              viewDataSource: vds ? { ...vds, tab } : undefined,
            };
          },
          replace: true,
        });
      },
      [navigate],
    ),
    setHistoryTable: useCallback(
      (sourceStreamName: string | undefined) => {
        navigate({
          search: (old) => {
            const vds = normalizeViewDataSource(old?.viewDataSource);
            return {
              ...old,
              viewDataSource: vds
                ? {
                    ...vds,
                    table: sourceStreamName,
                    run: undefined,
                  }
                : undefined,
            };
          },
          replace: true,
        });
      },
      [navigate],
    ),
    setHistoryRun: useCallback(
      (run: string | undefined) => {
        navigate({
          search: (old) => {
            const vds = normalizeViewDataSource(old?.viewDataSource);
            return {
              ...old,
              viewDataSource: vds ? { ...vds, run } : undefined,
            };
          },
          replace: true,
        });
      },
      [navigate],
    ),
    setHistoryTableAndRun: useCallback(
      (table: string, run: string) => {
        navigate({
          search: (old) => {
            const vds = normalizeViewDataSource(old?.viewDataSource);
            return {
              ...old,
              viewDataSource: vds ? { ...vds, table, run } : undefined,
            };
          },
          replace: true,
        });
      },
      [navigate],
    ),
    getLinkProps: (options: SlideOverInputOptions) => {
      return {
        viewDataSource: {
          syncId: options.syncId,
          tab: options.tab,
          table: options.sourceStreamName,
          run: options.run,
        },
      };
    },
  };
}

export function useViewDataSourceSearch() {
  const search = useSearch<LocationGenerics>();
  return normalizeViewDataSource(search.viewDataSource);
}
